// Detalle de una solicitud antes de ser campaña: completar el brief, aprobar y armar la campaña.
import * as React from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import {
  Boton,
  Dialog,
  DialogContenido,
  DialogDescripcion,
  DialogEncabezado,
  DialogPie,
  DialogTitulo,
  Esqueleto,
  Pill,
  TituloPagina,
  Vacio,
} from "@iavmx/ui";
import { useAccion, useCatalogo, useConSesion, useEsMercadotecnia, useSolicitud } from "./consultas";
import { faltantesDe, FormularioBrief, type Brief } from "./FormularioBrief";
import { Dato, EncabezadoSeccion, Migas, Nota } from "./componentes";
import { HistorialLista } from "./Historial";
import { ETIQUETA_CAMPO, ETIQUETA_CANAL, fechaLarga } from "./util";

const ETIQUETA_ESTADO: Record<string, string> = {
  nueva: "Por asignar",
  incompleta: "Incompleta",
  asignada: "Asignada",
  en_proceso: "En producción",
  en_revision: "En revisión",
  entregada: "Entregada",
  cerrada: "Cerrada",
  cancelada: "Cancelada",
};

export function Solicitud() {
  const { id } = useParams({ strict: false }) as { id: string };
  const conSesion = useConSesion();
  const esMkt = useEsMercadotecnia();
  const q = useSolicitud(id);
  const cat = useCatalogo();
  const navegar = useNavigate();
  const [editando, setEditando] = React.useState(false);
  const [brief, setBrief] = React.useState<Brief | null>(null);
  const [confirmarCancelar, setConfirmarCancelar] = React.useState(false);

  const guardar = useAccion<Brief, { estado: string; camposFaltantes: string[] }>({
    fn: (api, b) =>
      api.api.solicitudes[":id"].$patch({
        param: { id },
        json: { ...b, entregables: b.entregables.map((e) => ({ ...e, tipo: e.tipo as never })) },
      }),
    invalidar: [["solicitud", id], "bandeja"],
    exito: (r) => (r.camposFaltantes.length > 0 ? "Guardado. Sigue incompleta." : "Guardado. El brief está completo."),
    onExito: () => setEditando(false),
  });
  const armar = useAccion<void, { campanaId: string }>({
    fn: (api) => api.api.solicitudes[":id"].campana.$post({ param: { id } }),
    invalidar: ["bandeja", ["solicitud", id]],
    exito: "Campaña creada. Ahora desglosa las tareas.",
    onExito: (r) => void navegar({ to: "/campanas/$id", params: { id: r.campanaId } }),
  });
  const cancelar = useAccion<void>({
    fn: (api) => api.api.solicitudes[":id"].cancelar.$post({ param: { id }, json: {} }),
    invalidar: ["bandeja", ["solicitud", id]],
    exito: "Solicitud cancelada.",
    onExito: () => void navegar({ to: "/campanas" }),
  });

  if (!conSesion) return <Vacio titulo="Entra para ver la solicitud" />;
  if (q.isLoading || cat.isLoading) return <Esqueleto className="h-64" />;
  if (q.error || !q.data) return <Vacio titulo="No se pudo cargar" texto={q.error?.message} />;
  const d = q.data;
  const s = d.solicitud;
  const faltan = s.camposFaltantes as string[];
  const abierta = !["cerrada", "cancelada"].includes(s.estado);
  const briefActual: Brief = {
    titulo: s.titulo,
    objetivo: s.objetivo,
    publico: s.publico,
    productos: s.productos,
    fechaPublicacion: s.fechaPublicacion,
    areaSolicitante: s.areaSolicitanteClave,
    solicitanteId: s.solicitanteId,
    entregables: d.entregables.map((e) => ({ nombre: e.nombre, tipo: e.tipo, area: cat.data?.areas.find((a) => a.id === e.areaProductoraId)?.clave ?? null })),
    presupuesto: s.presupuesto != null ? Number(s.presupuesto) : null,
    prioridad: s.prioridad,
    materialesDelSolicitante: [],
  };

  return (
    <>
      <Migas partes={[{ etiqueta: "Solicitudes", to: "/campanas" }, { etiqueta: s.titulo }]} />
      <TituloPagina
        descripcion={`Folio ${s.folio} · llegó por ${ETIQUETA_CANAL[s.canalOrigen] ?? s.canalOrigen}${s.origenIa ? " · propuesta por la IA y aprobada por Mercadotecnia" : ""}`}
        accion={
          d.campana ? (
            <Boton variante="acento" asChild>
              <Link to="/campanas/$id" params={{ id: d.campana.id }}>
                Abrir campaña <ArrowRight />
              </Link>
            </Boton>
          ) : abierta && !s.completa ? (
            // Incompleta: lo único que toca hacer es completar el brief; armar la campaña ni aparece.
            !editando ? (
              <Boton variante="acento" onClick={() => { setBrief(briefActual); setEditando(true); }}>
                Completar brief
              </Boton>
            ) : null
          ) : esMkt && abierta ? (
            <Boton variante="acento" disabled={armar.isPending} onClick={() => armar.mutate()}>
              {armar.isPending ? "Creando…" : "Armar campaña"}
            </Boton>
          ) : null
        }
      >
        {s.titulo}
      </TituloPagina>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Pill tono={s.estado === "incompleta" ? "ambar" : s.estado === "nueva" ? "azul" : "neutro"}>{ETIQUETA_ESTADO[s.estado] ?? s.estado}</Pill>
        {s.esUrgente && <Pill tono="rojo">Urgente</Pill>}
        {s.presupuestoOculto && <Pill tono="neutro">Presupuesto oculto para tu rol</Pill>}
      </div>

      {s.estado === "incompleta" && (
        <Nota tono="ambar" className="mb-5">
          <p className="font-semibold text-tinta">Incompleta: no se asigna hasta completar el brief mínimo.</p>
          <p className="mt-1">Falta {faltan.map((c) => ETIQUETA_CAMPO[c]?.toLowerCase() ?? c).join(", ")}.{d.solicitante ? ` Se le pidió a ${d.solicitante}.` : ""}</p>
        </Nota>
      )}
      {s.estado === "nueva" && !d.campana && (
        <Nota tono="azul" className="mb-5">
          Brief completo. {esMkt ? "Arma la campaña para desglosar tareas y asignar áreas." : "Mercadotecnia armará la campaña."}
        </Nota>
      )}

      {editando && cat.data && brief ? (
        <form
          className="mb-8 grid max-w-3xl gap-6 border border-linea p-4 sm:p-5"
          onSubmit={(e) => {
            e.preventDefault();
            guardar.mutate(brief);
          }}
        >
          <EncabezadoSeccion>Completar el brief</EncabezadoSeccion>
          <FormularioBrief valor={brief} onChange={setBrief} catalogo={cat.data} idBase="editar" verPresupuesto={!s.presupuestoOculto} />
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Boton type="button" variante="contorno" onClick={() => setEditando(false)}>
              Cancelar
            </Boton>
            <Boton type="submit" variante="acento" disabled={guardar.isPending || !brief.titulo.trim()}>
              {guardar.isPending ? "Guardando…" : faltantesDe(brief).length > 0 ? "Guardar (sigue incompleta)" : "Guardar brief completo"}
            </Boton>
          </div>
        </form>
      ) : (
        <section className="mb-8 grid gap-4 border border-linea p-4 sm:grid-cols-2 sm:p-5 lg:grid-cols-3">
          <Dato etiqueta="Área solicitante">
            {d.areaSolicitante ?? <span className="text-ambar">Falta</span>}
            {d.solicitante && <span className="text-tinta-tenue"> · {d.solicitante}</span>}
          </Dato>
          <Dato etiqueta="Fecha de publicación">{s.fechaPublicacion ? fechaLarga(s.fechaPublicacion) : <span className="text-ambar">Falta</span>}</Dato>
          <Dato etiqueta="Producto">{s.productos.length > 0 ? s.productos.join(" · ") : <span className="text-ambar">Falta</span>}</Dato>
          <Dato etiqueta="Objetivo" className="sm:col-span-2">{s.objetivo ?? <span className="text-ambar">Falta</span>}</Dato>
          <Dato etiqueta="Público">{s.publico ?? <span className="text-tinta-tenue">No viene en el mensaje</span>}</Dato>
          <Dato etiqueta="Entregables" className="sm:col-span-2 lg:col-span-3">
            {d.entregables.length > 0 ? (
              <ul className="grid gap-1 sm:grid-cols-2">
                {d.entregables.map((e) => (
                  <li key={e.id}>
                    {e.nombre} <span className="text-tinta-tenue">· {e.areaProductora ?? "sin área"}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <span className="text-ambar">Falta</span>
            )}
          </Dato>
          {!s.presupuestoOculto && <Dato etiqueta="Presupuesto">{s.presupuesto != null ? `$${Number(s.presupuesto).toLocaleString("es-MX")} MXN` : "Sin definir"}</Dato>}
          {abierta && !d.campana && (
            <div className="flex flex-wrap gap-2 sm:col-span-2 lg:col-span-3">
              {s.completa && (
                <Boton variante="contorno" onClick={() => { setBrief(briefActual); setEditando(true); }}>
                  Editar brief
                </Boton>
              )}
              {esMkt && (
                <Boton variante="fantasma" onClick={() => setConfirmarCancelar(true)}>
                  Cancelar solicitud
                </Boton>
              )}
            </div>
          )}
        </section>
      )}

      {s.textoOriginal && (
        <section className="mb-8">
          <EncabezadoSeccion>Mensaje original</EncabezadoSeccion>
          <blockquote className="whitespace-pre-line border-l-2 border-linea-fuerte bg-fondo-suave px-4 py-3 font-ui text-sm text-tinta-2">{s.textoOriginal}</blockquote>
        </section>
      )}

      <section>
        <EncabezadoSeccion>Historial</EncabezadoSeccion>
        <HistorialLista eventos={d.historial} />
      </section>

      <Dialog open={confirmarCancelar} onOpenChange={setConfirmarCancelar}>
        <DialogContenido>
          <DialogEncabezado>
            <DialogTitulo>¿Cancelar la solicitud?</DialogTitulo>
            <DialogDescripcion>Deja de aparecer en la bandeja. El historial se conserva; nada se borra.</DialogDescripcion>
          </DialogEncabezado>
          <DialogPie>
            <Boton variante="contorno" onClick={() => setConfirmarCancelar(false)}>
              Volver
            </Boton>
            <Boton variante="peligro" disabled={cancelar.isPending} onClick={() => cancelar.mutate()}>
              Sí, cancelar
            </Boton>
          </DialogPie>
        </DialogContenido>
      </Dialog>
    </>
  );
}
